import { useEffect, useCallback, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";

interface SidePanelProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export function SidePanel({ children, title, subtitle }: SidePanelProps) {
  const { panelOpen, panelExpanded, setPanelOpen, setPanelExpanded, navBack, navForward, navIndex, navHistory } = useApp();
  const [panelWidth, setPanelWidth] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem("argus-panel-width");
      return stored ? Number(stored) : null;
    } catch { return null; }
  });
  const dragging = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      if (panelExpanded) {
        setPanelExpanded(false);
      } else {
        setPanelOpen(false);
      }
    }
  }, [panelExpanded, setPanelExpanded, setPanelOpen]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Drag-to-resize logic
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (panelExpanded) return;
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      const newWidth = window.innerWidth - ev.clientX;
      const clamped = Math.max(280, Math.min(newWidth, window.innerWidth * 0.7));
      setPanelWidth(clamped);
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      try {
        const el = panelRef.current;
        if (el) localStorage.setItem("argus-panel-width", String(el.offsetWidth));
      } catch { /* noop */ }
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [panelExpanded]);

  // Reset custom width only when expanded (fullscreen ignores custom width)
  // Keep it when panel closes so it's restored on next open

  if (!panelOpen) return null;

  return (
    <div
      ref={panelRef}
      className={`side-panel open${panelExpanded ? " expanded" : ""}`}
      style={!panelExpanded && panelWidth ? { width: panelWidth, minWidth: panelWidth, maxWidth: panelWidth } : undefined}
      data-testid="side-panel"
    >
      <div className="side-panel-resize" onMouseDown={onMouseDown} />

      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border bg-bg3 shrink-0">
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          {title && <div className="text-[11px] text-accent font-semibold uppercase tracking-wide">{title}</div>}
          {subtitle && <div className="text-base font-semibold text-text truncate">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            className="w-7 h-7 rounded-default bg-transparent border border-transparent text-text2 cursor-pointer flex items-center justify-center transition-[background,color,border-color] duration-[120ms] hover:bg-bg3 hover:text-text hover:border-border disabled:opacity-50 disabled:cursor-default [&>svg]:w-4 [&>svg]:h-4"
            onClick={navBack}
            disabled={navIndex <= 0}
            aria-label="Back"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10 3L5 8l5 5" /></svg>
          </button>
          <button
            className="w-7 h-7 rounded-default bg-transparent border border-transparent text-text2 cursor-pointer flex items-center justify-center transition-[background,color,border-color] duration-[120ms] hover:bg-bg3 hover:text-text hover:border-border disabled:opacity-50 disabled:cursor-default [&>svg]:w-4 [&>svg]:h-4"
            onClick={navForward}
            disabled={navIndex >= navHistory.length - 1}
            aria-label="Forward"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 3l5 5-5 5" /></svg>
          </button>
          <button
            className="w-7 h-7 rounded-default bg-transparent border border-transparent text-text2 cursor-pointer flex items-center justify-center transition-[background,color,border-color] duration-[120ms] hover:bg-bg3 hover:text-text hover:border-border disabled:opacity-50 disabled:cursor-default [&>svg]:w-4 [&>svg]:h-4"
            onClick={() => setPanelExpanded(!panelExpanded)}
            aria-label={panelExpanded ? "Collapse panel" : "Expand panel"}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              {panelExpanded
                ? <><line x1="14" y1="10" x2="21" y2="3" /><polyline points="20 10 14 10 14 4" /><line x1="3" y1="21" x2="10" y2="14" /><polyline points="4 14 10 14 10 20" /></>
                : <><line x1="10" y1="14" x2="3" y2="21" /><polyline points="3 15 3 21 9 21" /><line x1="14" y1="10" x2="21" y2="3" /><polyline points="21 9 21 3 15 3" /></>
              }
            </svg>
          </button>
          <button
            className="w-7 h-7 rounded-default bg-transparent border border-transparent text-text2 cursor-pointer flex items-center justify-center transition-[background,color,border-color] duration-[120ms] hover:bg-bg3 hover:text-text hover:border-border disabled:opacity-50 disabled:cursor-default [&>svg]:w-4 [&>svg]:h-4"
            onClick={() => setPanelOpen(false)}
            aria-label="Close panel"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5 animate-[sidepanel-fadein_0.15s_ease_forwards]">
        {children}
      </div>
    </div>
  );
}
